import React, { useEffect, useRef } from "react";
import { Box, Button, ButtonBase, Dialog, Stack, Typography } from "@mui/material";
import { keyframes } from "@emotion/react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { COLORS } from "./constants";
import { primaryButtonSx, ghostButtonSx } from "./styles";
import { platformIcon, platformName } from "./utils";
import { CopyableField } from "./CopyableField";
import { ProblemAlerts } from "./ProblemAlerts";
import { Mark, Wordmark } from "../Logo";

// ─────────────────────────────────────────────────────────────────────────────
// Onboarding — first-time setup in three steps
//
//   1. Where to stream   connect YouTube/Facebook, or add a key-based platform
//   2. Connect OBS       server + stream key, and where they go in OBS
//   3. Go live           listens for the encoder and confirms the first stream
//
// Nothing here is a mock: every button drives the same code the Studio uses
// (connect flow, destinations editor, /token/generate, /api/stream-status), so
// what the user sets up here IS their setup. Matches the "First-time setup"
// board on the design canvas.
// ─────────────────────────────────────────────────────────────────────────────

// Index into ManageDestinationsDialog's platform list (OutputDestinationsPanel
// PLATFORM_CARDS) so each button opens the editor on the right platform.
const KEY_PLATFORMS = [
  { kind: "tiktok",    label: "TikTok",    tab: 2 },
  { kind: "linkedin",  label: "LinkedIn",  tab: 3 },
  { kind: "x",         label: "X",         tab: 4 },
  { kind: "instagram", label: "Instagram", tab: 5 },
  { kind: "kick",      label: "Kick",      tab: 6 },
  { kind: "rtmp",      label: "Custom",    tab: 7 }
];

const ring = keyframes`
  0%   { transform: scale(.35); opacity: .9; }
  100% { transform: scale(1);   opacity: 0;  }
`;

const STEPS = ["Where to stream", "Connect OBS", "Go live"];

const eyebrowSx = {
  display: "flex", alignItems: "center", gap: 1, m: 0,
  font: `600 12px/1 ${COLORS.fontSans}`, letterSpacing: ".1em", textTransform: "uppercase",
  color: COLORS.muted,
  "&::before": {
    content: '""', width: 0, height: 0,
    borderLeft: "5px solid transparent", borderRight: "5px solid transparent",
    borderBottom: `8px solid ${COLORS.cyan}`
  }
};

const cardSx = {
  bgcolor: COLORS.panel, border: `1px solid ${COLORS.border}`, borderRadius: "24px",
  p: { xs: 2.5, sm: 4 }, display: "flex", flexDirection: "column", gap: 3,
  boxSizing: "border-box", minWidth: 0
};

const h2Sx = {
  mt: 1, fontFamily: COLORS.fontDisplay, fontWeight: 700, letterSpacing: "-0.02em",
  fontSize: { xs: 24, sm: 30 }, lineHeight: 1.15, color: COLORS.text
};

const leadSx = { mt: 1, fontSize: 16, lineHeight: 1.55, color: COLORS.textSoft, maxWidth: 620 };

const platSx = (on) => ({
  display: "flex", alignItems: "center", gap: 1.5, p: 1.75, width: "100%",
  borderRadius: "16px", textAlign: "left", color: COLORS.text, fontFamily: COLORS.fontSans,
  border: `1px solid ${on ? "rgba(61,214,140,0.34)" : COLORS.border}`,
  bgcolor: on ? "rgba(61,214,140,0.12)" : "#211F4E",
  "&:hover": { borderColor: on ? "rgba(61,214,140,0.5)" : COLORS.borderStrong },
  "&.Mui-focusVisible": { outline: `3px solid ${COLORS.cyan}`, outlineOffset: 2 }
});

const tagSx = {
  font: `600 11px ${COLORS.fontSans}`, letterSpacing: ".06em", textTransform: "uppercase",
  color: COLORS.cyan, px: 1, py: "3px", borderRadius: 99, bgcolor: "rgba(24,165,152,0.14)",
  flex: "none"
};

const footerSx = {
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5,
  flexWrap: "wrap", pt: 2, borderTop: `1px solid ${COLORS.border}`
};

const bigBtn = { height: 44, px: 2.5, fontSize: 15, borderRadius: "12px" };

function Stepper({ step, done }) {
  return (
    <Box component="ol" aria-label="Setup progress"
      sx={{ listStyle: "none", m: 0, p: 0, display: "flex", gap: 2, alignItems: "center" }}>
      {STEPS.map((title, i) => {
        const n = i + 1;
        const isDone = step > n || (n === 3 && done);
        const isOn = step === n && !isDone;
        const last = n === STEPS.length;
        return (
          <Box component="li" key={title} aria-current={isOn ? "step" : undefined}
            sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: last ? "0 1 auto" : "1 1 0", minWidth: 0 }}>
            <Box sx={{
              width: 34, height: 34, borderRadius: "50%", flex: "none",
              display: "flex", alignItems: "center", justifyContent: "center",
              font: `700 14px ${COLORS.fontSans}`,
              border: "1.5px solid",
              borderColor: isOn ? COLORS.accent : isDone ? "rgba(43,189,175,0.42)" : COLORS.borderStrong,
              bgcolor: isOn ? COLORS.accent : isDone ? "rgba(24,165,152,0.14)" : "transparent",
              color: isOn ? COLORS.onAccent : isDone ? COLORS.cyan : COLORS.muted
            }}>
              {isDone ? <CheckIcon sx={{ fontSize: 18 }} /> : n}
            </Box>
            <Typography sx={{
              fontWeight: 600, fontSize: 14, whiteSpace: "nowrap",
              color: isOn || isDone ? COLORS.text : COLORS.muted,
              display: { xs: isOn ? "block" : "none", sm: "block" }
            }}>
              {title}
            </Typography>
            {!last && (
              <Box sx={{ flex: 1, height: 2, borderRadius: 2, minWidth: 12,
                bgcolor: isDone ? "rgba(43,189,175,0.42)" : COLORS.borderStrong }} />
            )}
          </Box>
        );
      })}
    </Box>
  );
}

function Check({ done, children }) {
  return (
    <Box component="li" sx={{ display: "flex", alignItems: "center", gap: 1.5, fontSize: 15, color: COLORS.text }}>
      <Box sx={{
        width: 24, height: 24, borderRadius: "50%", flex: "none",
        display: "flex", alignItems: "center", justifyContent: "center",
        ...(done
          ? { bgcolor: "rgba(61,214,140,0.12)", color: COLORS.green }
          : { border: `1.5px dashed ${COLORS.borderStrong}` })
      }}>
        {done && <CheckIcon sx={{ fontSize: 16 }} />}
      </Box>
      {children}
    </Box>
  );
}

const listNames = (names) => {
  if (names.length <= 2) return names.join(" and ");
  return `${names.slice(0, 2).join(", ")} and ${names.length - 2} more`;
};

export function Onboarding({
  open, firstName, step, setStep, onSkip, onFinish,
  platforms, platformConnections, connectBusy, onConnect, onAddWithKey,
  rtmpUrl, streamKey, keyExpiresAt, onGetKey, copiedField, copyToClipboard,
  ingestLive, streamStatus, metrics, problems, onProblemFix, onProblemDismiss,
  onChooseSource
}) {
  const yt = platformConnections?.youtube;
  const fb = platformConnections?.facebook;
  const keyed = platforms.filter((p) => p.id !== "YouTube-Connected" && p.id !== "Facebook-Connected");

  // Step 2 needs a key: fetch one on arrival (the server reuses a valid one,
  // so coming back to this step never invalidates what is already in OBS).
  const asked = useRef(false);
  useEffect(() => {
    if (!open || step !== 2) return;
    if (streamKey || asked.current) return;
    asked.current = true;
    onGetKey();
  }, [open, step, streamKey, onGetKey]);

  // Only problems raised since the user reached step 3 belong to this attempt.
  // A minute of slack covers clock drift between browser and server.
  const step3At = useRef(0);
  useEffect(() => { if (step === 3) step3At.current = Date.now(); }, [step]);
  const freshProblems = (problems || []).filter((p) => p.at >= step3At.current - 60_000);

  const liveNames = platforms
    .filter((p) => streamStatus?.[p.id] === "live")
    .map((p) => (p.kind === "rtmp" ? p.label : platformName(p.kind)));
  const live = step === 3 && ingestLive && liveNames.length > 0;
  const arriving = step === 3 && ingestLive && !live;

  const keyExpired = keyExpiresAt && keyExpiresAt < Date.now();
  const keyUntil = keyExpiresAt
    ? new Date(keyExpiresAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : null;

  // In this flow "fix the key" means going back to the key step, not to the
  // Studio's encoder panel behind the dialog.
  const handleFix = (problem) => {
    if (problem?.fix?.type === "source") { setStep(2); onGetKey(); return; }
    onProblemFix(problem);
  };

  return (
    <Dialog
      open={open}
      fullScreen
      aria-labelledby="onboarding-title"
      PaperProps={{ sx: { bgcolor: COLORS.bg, backgroundImage: "none", color: COLORS.text, textAlign: "left" } }}
    >
      <Box sx={{ minHeight: "100%", display: "flex", flexDirection: "column", fontFamily: COLORS.fontSans }}>
        <Box component="header" sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2,
          px: { xs: 2, sm: 5 }, py: 3
        }}>
          <Wordmark height={30} />
          <Button onClick={onSkip}
            sx={{ textTransform: "none", fontWeight: 600, fontSize: 14, color: COLORS.muted,
              "&:hover": { color: COLORS.text, bgcolor: "transparent" } }}>
            Skip setup
          </Button>
        </Box>

        <Box component="main" sx={{
          flex: 1, width: "100%", maxWidth: 940, mx: "auto", boxSizing: "border-box",
          px: { xs: 2, sm: 3 }, pt: 2, pb: 8, display: "flex", flexDirection: "column", gap: 4
        }}>
          <Box>
            <Typography component="p" sx={eyebrowSx}>
              Welcome to Fennira{firstName ? `, ${firstName}` : ""}
            </Typography>
            <Typography id="onboarding-title" component="h1" sx={{
              mt: 1.75, fontFamily: COLORS.fontDisplay, fontWeight: 700,
              fontSize: { xs: 34, sm: 48 }, lineHeight: 1.05, letterSpacing: "-0.03em"
            }}>
              Let's get you live in three steps
            </Typography>
          </Box>

          <Stepper step={step} done={live} />

          {/* ── Step 1 ─────────────────────────────────────────────── */}
          {step === 1 && (
            <Box component="section" aria-labelledby="ob-s1" sx={cardSx}>
              <Box>
                <Typography component="p" sx={eyebrowSx}>Step 1 of 3</Typography>
                <Typography id="ob-s1" component="h2" sx={h2Sx}>Where should your stream go?</Typography>
                <Typography sx={leadSx}>
                  Pick at least one. Connecting an account is easiest: Fennira creates the
                  broadcast for you, so there's no key to copy.
                </Typography>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1.5 }}>
                {[
                  { id: "youtube", label: "YouTube", conn: yt,
                    idle: "Sign in · we create the broadcast", tab: 0 },
                  { id: "facebook", label: "Facebook", conn: fb,
                    idle: "Sign in · pick a Page", tab: 1 }
                ].map(({ id, label, conn, idle, tab }) => {
                  const on = !!conn?.connected;
                  const busy = connectBusy === id;
                  const name = conn?.selectedName || conn?.channelTitle || conn?.pageName;
                  return (
                    <Box key={id}>
                      <ButtonBase
                        onClick={() => (on ? onAddWithKey(tab) : onConnect(id))}
                        disabled={busy}
                        aria-label={on ? `${label} connected${name ? ` as ${name}` : ""}. Open settings` : `Connect ${label}`}
                        sx={platSx(on)}
                      >
                        {platformIcon(id, 40)}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: 15 }}>{label}</Typography>
                          <Typography noWrap sx={{ fontSize: 12, color: COLORS.muted, mt: 0.25 }}>
                            {busy ? "Opening sign-in…" : on ? `Connected${name ? ` · ${name}` : ""}` : idle}
                          </Typography>
                        </Box>
                        {on
                          ? <CheckCircleIcon sx={{ color: COLORS.green, fontSize: 22 }} />
                          : <Box component="span" sx={tagSx}>Connect</Box>}
                      </ButtonBase>
                      {!on && (
                        <Button size="small" onClick={() => onAddWithKey(tab)}
                          sx={{ mt: 0.5, textTransform: "none", fontSize: 12, color: COLORS.muted,
                            "&:hover": { color: COLORS.text, bgcolor: "transparent" } }}>
                          Use a stream key instead
                        </Button>
                      )}
                    </Box>
                  );
                })}
              </Box>

              <Box>
                <Typography sx={{ mb: 1.5, fontSize: 13, fontWeight: 600, color: COLORS.muted }}>
                  Or add any of these with a server URL and stream key
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(3, minmax(0, 1fr))", sm: "repeat(6, minmax(0, 1fr))" }, gap: 1.25 }}>
                  {KEY_PLATFORMS.map(({ kind, label, tab }) => {
                    const on = keyed.some((p) => p.kind === kind);
                    return (
                      <ButtonBase key={kind} onClick={() => onAddWithKey(tab)}
                        aria-label={`${on ? "Edit" : "Add"} ${label}`}
                        sx={{ ...platSx(on), flexDirection: "column", gap: 1, p: "14px 8px", textAlign: "center",
                          position: "relative" }}>
                        {platformIcon(kind, 30)}
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{label}</Typography>
                        {on && <CheckCircleIcon sx={{ position: "absolute", top: 6, right: 6, color: COLORS.green, fontSize: "16px !important" }} />}
                      </ButtonBase>
                    );
                  })}
                </Box>
              </Box>

              <Box sx={footerSx}>
                <Typography sx={{ fontSize: 14, color: COLORS.muted }} role="status">
                  {platforms.length
                    ? `${platforms.length} destination${platforms.length === 1 ? "" : "s"} ready`
                    : "Choose at least one destination to continue"}
                </Typography>
                <Button onClick={() => setStep(2)} disabled={!platforms.length}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ ...primaryButtonSx, ...bigBtn,
                    "&.Mui-disabled": { bgcolor: COLORS.accent, color: COLORS.onAccent, opacity: 0.45 } }}>
                  Continue
                </Button>
              </Box>
            </Box>
          )}

          {/* ── Step 2 ─────────────────────────────────────────────── */}
          {step === 2 && (
            <Box component="section" aria-labelledby="ob-s2" sx={cardSx}>
              <Box>
                <Typography component="p" sx={eyebrowSx}>Step 2 of 3</Typography>
                <Typography id="ob-s2" component="h2" sx={h2Sx}>Point OBS at Fennira</Typography>
                <Typography sx={leadSx}>
                  Copy these two values into OBS. The key is private, like a password:
                  anyone with it can stream to your channels.
                </Typography>
              </Box>

              {streamKey ? (
                <Stack spacing={1.5}>
                  <CopyableField label="Server" value={rtmpUrl} fieldId="obServer"
                    copiedField={copiedField} onCopy={copyToClipboard} />
                  <CopyableField label="Stream key" value={streamKey} fieldId="obKey" secret
                    copiedField={copiedField} onCopy={copyToClipboard} />
                  {keyUntil && (
                    <Typography sx={{ fontSize: 13, color: keyExpired ? COLORS.amber : COLORS.muted }}>
                      {keyExpired
                        ? "This key has expired. "
                        : `Start your stream before ${keyUntil}: a key is valid for one hour. `}
                      {keyExpired && (
                        <Button size="small" onClick={onGetKey}
                          sx={{ textTransform: "none", p: 0, minWidth: 0, color: COLORS.cyan, fontWeight: 600, verticalAlign: "baseline" }}>
                          Get a fresh key
                        </Button>
                      )}
                    </Typography>
                  )}
                </Stack>
              ) : (
                <Typography sx={{ color: COLORS.muted, fontSize: 14 }} role="status">Preparing your stream key…</Typography>
              )}

              <Box component="ol" sx={{
                listStyle: "none", m: 0, p: 2.5, borderRadius: "16px", bgcolor: "#211F4E",
                border: `1px solid ${COLORS.border}`, display: "flex", flexDirection: "column", gap: 1.75
              }}>
                {[
                  <>In OBS, open <b>Settings → Stream</b>.</>,
                  <>Set <b>Service</b> to <b>Custom…</b></>,
                  <>Paste the server and key above, then click <b>OK</b>.</>
                ].map((text, i) => (
                  <Box component="li" key={i} sx={{ display: "flex", gap: 1.75, fontSize: 15, lineHeight: 1.5, color: COLORS.text }}>
                    <Box sx={{ flex: "none", width: 28, height: 28, borderRadius: "50%",
                      bgcolor: "rgba(24,165,152,0.14)", color: COLORS.cyan, font: `700 13px ${COLORS.fontSans}`,
                      display: "flex", alignItems: "center", justifyContent: "center" }}>{i + 1}</Box>
                    <Box component="span" sx={{ "& b": { color: COLORS.text } }}>{text}</Box>
                  </Box>
                ))}
              </Box>

              <Typography sx={{ fontSize: 14, color: COLORS.muted }}>
                Not using OBS? You can also stream{" "}
                {[["browser", "from this browser"], ["srt", "over SRT"], ["file", "a scheduled MP4"]].map(([kind, text], i) => (
                  <React.Fragment key={kind}>
                    {i === 2 ? ", or " : i === 1 ? ", " : ""}
                    <Box component="button" type="button" onClick={() => onChooseSource(kind)}
                      sx={{ p: 0, border: 0, bgcolor: "transparent", font: "inherit", cursor: "pointer",
                        color: COLORS.cyan, textDecoration: "underline", textUnderlineOffset: 3 }}>
                      {text}
                    </Box>
                  </React.Fragment>
                ))}.
              </Typography>

              <Box sx={footerSx}>
                <Button onClick={() => setStep(1)} sx={{ ...ghostButtonSx, ...bigBtn }}>Back</Button>
                <Button onClick={() => setStep(3)} disabled={!streamKey} endIcon={<ArrowForwardIcon />}
                  sx={{ ...primaryButtonSx, ...bigBtn,
                    "&.Mui-disabled": { bgcolor: COLORS.accent, color: COLORS.onAccent, opacity: 0.45 } }}>
                  I've pasted them
                </Button>
              </Box>
            </Box>
          )}

          {/* ── Step 3 ─────────────────────────────────────────────── */}
          {step === 3 && (
            <Box component="section" aria-labelledby="ob-s3" sx={cardSx}>
              <Box>
                <Typography component="p" sx={eyebrowSx}>Step 3 of 3</Typography>
                <Typography id="ob-s3" component="h2" sx={h2Sx}>
                  {live ? `You're live on ${listNames(liveNames)}`
                    : arriving ? "Video is arriving…" : "Listening for OBS…"}
                </Typography>
                <Typography sx={leadSx}>
                  {live
                    ? "Video is arriving from OBS and going out to your channels. From now on, the Studio is your home base."
                    : arriving
                      ? "OBS is connected. Fennira is starting your destinations, which takes a few seconds."
                      : "Press Start Streaming in OBS. We'll spot it within a few seconds."}
                </Typography>
              </Box>

              <ProblemAlerts problems={freshProblems} onFix={handleFix} onDismiss={onProblemDismiss} />

              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 5 }}>
                {live ? (
                  <Box sx={{ width: 132, height: 132, borderRadius: "50%", flex: "none",
                    bgcolor: "rgba(61,214,140,0.12)", boxShadow: "inset 0 0 0 1.5px rgba(61,214,140,0.34)",
                    display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <CheckIcon sx={{ fontSize: 56, color: COLORS.green }} />
                  </Box>
                ) : (
                  <Box aria-hidden sx={{ position: "relative", width: 132, height: 132, flex: "none" }}>
                    {[0, 0.8, 1.6].map((delay) => (
                      <Box key={delay} sx={{
                        position: "absolute", inset: 0, borderRadius: "50%",
                        border: `1.5px solid ${COLORS.cyan}`, opacity: 0,
                        animation: `${ring} 2.4s ease-out ${delay}s infinite`,
                        "@media (prefers-reduced-motion: reduce)": { animation: "none", opacity: 0.3 }
                      }} />
                    ))}
                    <Box sx={{ position: "absolute", inset: "38px", borderRadius: "50%",
                      bgcolor: "rgba(24,165,152,0.14)", boxShadow: "inset 0 0 0 1px rgba(43,189,175,0.42)",
                      display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Mark size={30} />
                    </Box>
                  </Box>
                )}

                <Box component="ul" role="status"
                  sx={{ listStyle: "none", m: 0, p: 0, display: "flex", flexDirection: "column", gap: 2, flex: "1 1 280px" }}>
                  <Check done>
                    {platforms.length} destination{platforms.length === 1 ? "" : "s"} ready
                    {" · "}{listNames(platforms.map((p) => p.label))}
                  </Check>
                  <Check done>Server and key ready in OBS</Check>
                  <Check done={ingestLive}>
                    {ingestLive
                      ? `Video arriving${metrics?.fps ? ` · ${Math.round(metrics.fps)} fps` : ""}${metrics?.bitrate ? ` · ${(metrics.bitrate / 1000).toFixed(1)} Mbps` : ""}`
                      : "Waiting for video from OBS"}
                  </Check>
                </Box>
              </Box>

              <Box sx={footerSx}>
                <Button onClick={() => setStep(2)} sx={{ ...ghostButtonSx, ...bigBtn }}>Back</Button>
                {live ? (
                  <Button onClick={onFinish} endIcon={<ArrowForwardIcon />} sx={{ ...primaryButtonSx, ...bigBtn }}>
                    Open the Studio
                  </Button>
                ) : (
                  <Typography sx={{ fontSize: 14, color: COLORS.muted }}>
                    In OBS, click <Box component="b" sx={{ color: COLORS.text }}>Start Streaming</Box>
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}
